import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@services/snackbar/snackbar.service';

import { PageAction } from '../../../components/page-actions/page-actions.component';
import { Models } from '@ibexfusion/models';
import { CompositionAppService } from '@services/compositions/composition-app.service';
import { DialogService } from '@services/dialog/dialog.service';
import { CompositionService } from '@services/compositions/composition.service';
import { MemoryService } from '@services/memory/memory.service';
import { ImportService } from '@services/imports/import.service';

@Component({
    selector: 'app-composition-app-editor',
    templateUrl: './composition-app-editor.component.html',
    styleUrls: ['./composition-app-editor.component.scss'],
    providers: [CompositionService, ImportService, CompositionAppService, DialogService],
    standalone: false
})
export class CompositionAppEditorComponent implements OnInit {

  public pageActions: PageAction[];

  public pageTitle: string;

  public composition: Models.Composition;

  public app: Models.CompositionApp;

  public availableLanguages: Map<string, { active: boolean, key: string }> = new Map();

  public selectedLanguage: string;

  public appTranslations: Map<string, {
    translation: Models.CompositionAppTranslation,
    assignedItemsProvider: MemoryService<Models.ImportAssignableMenuItem>
  }> = new Map();

  public activatedLanguages: {
    translation: Models.CompositionAppTranslation,
    assignedItemsProvider: MemoryService<Models.ImportAssignableMenuItem>
  }[];

  public availableItemsProvider: MemoryService<Models.ImportAssignableMenuItem>;

  private availableItemsMap: Map<string, Models.ImportAssignableMenuItem>;

  constructor(
    private activatedRoute: ActivatedRoute, private compositionService: CompositionService, private compositionAppService: CompositionAppService,
    private importService: ImportService, private snackbar: SnackbarService, private router: Router, private dialogService: DialogService
  ) {
  }

  async ngOnInit() {
    this.createPageActions();

    await this.fetchComposition();
    const appId: string = this.activatedRoute.snapshot.params['appId'];

    if (!appId) {
      this.pageTitle = 'Create app for composition: ' + this.composition.title;
      this.app = new Models.CompositionApp();
      this.app.populateFromJSON({ compositionId: this.composition._id, icon: '', name: '', translations: [] });
      return;
    }
    this.pageTitle = 'Edit app from composition: ' + this.composition.title;
    this.pageActions.push(
      new PageAction({
        icon: 'info', title: 'Overview app',
        callback: () => {
          this.router.navigate(['compositions', this.activatedRoute.snapshot.params['id'], 'apps', this.activatedRoute.snapshot.params['appId']]);
        }
      }),
      new PageAction({
        icon: 'create_new_folder', title: 'Create app',
        callback: () => {
          this.router.navigate(['compositions', this.activatedRoute.snapshot.params['id'], 'apps', 'create']);
        }
      })
    );
    this.app = await this.compositionAppService.get(appId);
    for (let i = 0; i < this.app.translations.length; i++) {
      if (!this.app.translations[i].items) {
        this.app.translations[i].items = this.app.translations[i].menuItemIds;
      }
    }
    for (let i = 0; i < this.app.translations.length; i++) {
      const translation = this.app.translations[i];
      const assignedItems: Models.ImportAssignableMenuItem[] = [];
      for (let j = 0; j < translation.menuItemIds.length; j++) {
        const menuItemId = translation.menuItemIds[j];
        if (this.availableItemsMap && this.availableItemsMap.get(menuItemId)) {
          assignedItems.push(this.availableItemsMap.get(menuItemId));
        } else {
          assignedItems.push(<Models.ImportAssignableMenuItem>{ menuItemId: menuItemId, title: menuItemId });
        }
      }
      this.appTranslations.set(this.app.translations[i].language, {
        translation: this.app.translations[i],
        assignedItemsProvider: new MemoryService<Models.ImportAssignableMenuItem>(assignedItems)
      });

      this.availableLanguages.set(translation.language, { active: true, key: translation.language });
      this.setActivatedTranslations();
    }
  }

  public async languageChange(lang: string): Promise<any> {
    if (!this.availableLanguages.get(lang).active) {
      await this.addLanguage(lang);
      return;
    }
    const res = await this.dialogService.confirm('Remove language?');
    if (!res) {
      return;
    }

    for (let i = 0; i < this.app.translations.length; i++) {
      if (this.app.translations[i].language === lang) {
        this.app.translations.splice(i, 1);
      }
    }
    this.appTranslations.delete(lang);
    this.availableLanguages.set(lang, { active: false, key: lang });
    this.setActivatedTranslations();
  }

  public async addLanguage(lang: string): Promise<any> {
    const compositionAppTranslation = new Models.CompositionAppTranslation();
    compositionAppTranslation.populateFromJSON({ language: lang, menuItemIds: [], items: [], appTitle: '' });

    this.appTranslations.set(compositionAppTranslation.language, {
      translation: compositionAppTranslation, assignedItemsProvider: new MemoryService<Models.ImportAssignableMenuItem>()
    });

    this.selectedLanguage = undefined;
    this.availableLanguages.set(lang, { active: true, key: lang });
    this.setActivatedTranslations();
  }

  public async saveCompositionApp(): Promise<any> {
    this.app.translations = [];
    this.appTranslations.forEach((translationContent) => {
      this.app.translations.push(translationContent.translation);
    });

    try {
      const result = this.app._id ? await this.compositionAppService.update(this.app._id, this.app) : await this.compositionAppService.create(this.app);
      this.snackbar.success('App saved');
      this.app._id = this.app._id || result._id;
      await this.router.navigate(['compositions', this.composition._id, 'apps', this.app._id]);
    } catch (error) {
      this.snackbar.error('Failed');
      console.error(error);
    }
  }

  private async fetchComposition(): Promise<void> {
    this.composition = await this.compositionService.get(this.activatedRoute.snapshot.params['id']);
    this.composition.screen.availableLanguages.forEach(lang => {
      this.availableLanguages.set(lang, { active: false, key: lang });
    });

    const response = await this.importService.find(0, 5000, { compositionId: this.composition._id });
    const availableItems: Models.ImportAssignableMenuItem[] = [];
    response.data.filter(importer => !!importer.assignableMenuItems).forEach(importer => {
      importer.assignableMenuItems.forEach(item => {
        availableItems.push({ importTitle: importer.title, ...item } as any);
      });
    });
    this.availableItemsProvider = new MemoryService(availableItems);
    this.availableItemsMap = new Map(availableItems.filter(el => !!el).map(item => [item.menuItemId, item]));
  }

  private setActivatedTranslations(): void {
    this.activatedLanguages = [];
    this.availableLanguages.forEach(language => {
      if (language.active) {
        this.activatedLanguages.push(this.appTranslations.get(language.key));
      }
    });
  }

  private createPageActions(): void {
    this.pageActions = [
      new PageAction({
        icon: 'account_balance', title: 'Overview composition',
        callback: () => {
          this.router.navigate(['compositions', this.activatedRoute.snapshot.params['id']]);
        }
      })
    ];
  }
}
